import jp from 'jsonpath';
import type { AnnotatedJsonSchema, EntityRootData } from '@web-config-app/core';
import { pipeFunctions } from '@web-config-app/core';
import { cloneDeep, unset } from 'lodash-es';
import { regenerateGeneratedIds } from '../regenerate-generated-ids/regenerate-generated-ids';
import { transposeIdToSourceId } from '../transpose-id-to-source-id/transpose-id-to-source-id';
import { removeDomainEntityId } from '../remove-domain-entity-id/remove-domain-entity-id';

/**
 * Alters data for duplicating an entity.
 *
 * This function performs the following operations:
 * 1. Makes a deep copy of the entity data
 * 2. Removes the original ID (for non-config entities)
 * 3. Updates the entity name with a prefix
 * 4. Regenerates generated IDs based on schema annotations
 * 5. Removes the domain entity ID
 * 6. For config entities, moves the ID to sourceId
 *
 * @param {EntityRootData} [params.data] - The entity data to be cloned
 * @param {AnnotatedJsonSchema} params.schema - The entity schema for regenerating IDs
 * @param {string} [params.entityNamePath] - JSON path to the entity name
 * @param {string} params.prefix - The prefix to add to the entity name
 * @returns {EntityRootData|undefined} - The modified entity data ready for duplication
 */
export const alterDataForCloneEntity = ({
  data,
  schema,
  entityNamePath,
  prefix,
}: {
  data?: EntityRootData;
  schema: AnnotatedJsonSchema;
  entityNamePath?: string; // JSON path
  prefix: string;
}) => {
  if (typeof data === 'undefined') {
    return undefined;
  }

  // Need to clone the data object because jp.value and unset functions will alter the data passed in.
  const isolatedData = cloneDeep(data);

  // Handle non-config entities : remove the ID property from data because when duplicating an entity, we do not want to see the original entities ID
  if (isolatedData.id && data.type !== 'config-entity') {
    unset(isolatedData, 'id');
  }

  // Update the entity name with the prefix if path is provided
  const dataWithPrefixedName =
    entityNamePath && isolatedData
      ? (() => {
          const instanceName = jp.value(isolatedData, entityNamePath);
          jp.value(isolatedData, entityNamePath, `${prefix} ${instanceName}`);
          return isolatedData;
        })()
      : isolatedData;

  // Create a pipeline to process the data
  const processData = pipeFunctions(
    regenerateGeneratedIds,
    removeDomainEntityId,
  );

  const processedData = processData(dataWithPrefixedName, schema);

  // For config entities, transpose ID to sourceId
  if (data.type === 'config-entity') {
    // If we do not have id in the data, we do not need to transpose it
    if (!processedData.id) {
      return processedData;
    }
    return transposeIdToSourceId(processedData);
  }

  return processedData;
};
